import React, { useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { isNil, get, find, pickBy, includes, cloneWith } from "lodash";
import { useQuery } from "@apollo/client/react";
import useStableCallback from "src/hooks/useStableCallback";

import Auth0UserProps from "src/custom-prop-types/auth0user";
import { useOrgConfigForTenant } from "src/hooks/orgConfig/hooks/useOrgConfigForTenant";

import { ERROR_CODES } from "src/components/ErrorPage/detectErrorType";
import { setDefaultDecimalPlaces } from "src/util/toLocaleString";

import useRestricted from "src/app/Restricted/useRestricted";
import useTagsVisibleInFilter from "src/app/Restricted/useTagsVisibleInFilter";
import { ADMIN_ROLE } from "src/services/auth";
import useUserRole from "src/app/Restricted/useUserRole";
import { useWorkspaceId } from "../ApolloSettingsContext";
import {
  getWorkspaces as getWorkspacesQuery,
  getWorkspace as getWorkspaceQuery,
  getActiveWorkspace as getActiveWorkspaceQuery,
  getReportingInfraProvisioned as getReportingInfraProvisionedQuery,
} from "../context.graphql";

import { Provider, CHALLENGE_NONE } from "./WorkspaceContext";
import useDecidingWorkspaceAndChallenge from "./useDecidingWorkspaceAndChallenge";

const throwErrorIfSelectedWorkspaceDoesNotExist = (
  workspacesData,
  selectedWorkspaceId
) => {
  const workspaces = get(workspacesData, "workspaces", []);
  if (!find(workspaces, { id: selectedWorkspaceId })) {
    throw new Error(ERROR_CODES.WORKSPACE_NOT_FOUND);
  }
};

const WorkspaceContextProvider = (props) => {
  const { children } = props;

  const [isRestricted] = useRestricted();
  const userRole = useUserRole();

  const isAdmin = userRole === ADMIN_ROLE;

  const selectedWorkspaceId = useWorkspaceId();

  const {
    data: workspacesData,
    loading: loadingWorkspaces,
    error: workspacesQueryError,
  } = useQuery(getWorkspacesQuery);

  if (
    !loadingWorkspaces &&
    workspacesData &&
    selectedWorkspaceId &&
    !workspacesQueryError
  ) {
    throwErrorIfSelectedWorkspaceDoesNotExist(
      workspacesData,
      selectedWorkspaceId
    );
  }

  const {
    data: activeWorkspaceData,
    loading: loadingActiveWorkspace,
    error: activeWorkspaceDataError,
    refetch: _refetchActiveWorkspace,
  } = useQuery(getActiveWorkspaceQuery, {
    skip: !isNil(selectedWorkspaceId),
  });
  const refetchActiveWorkspace = useStableCallback(_refetchActiveWorkspace);

  const shouldGetWorkspaceReporting = !isRestricted;
  const {
    data: selectedWorkspaceData,
    loading: loadingSelectedWorkspace,
    error: selectedWorkspaceDataError,
    refetch: _refetchSelectedWorkspace,
  } = useQuery(getWorkspaceQuery, {
    variables: {
      id: selectedWorkspaceId,
      // Reporting infra can call AWS Batch; do not block bootstrap on it.
      includeReportingInfraProvisioned: false,
    },
    skip: isNil(selectedWorkspaceId),
  });
  const refetchSelectedWorkspace = useStableCallback(_refetchSelectedWorkspace);

  const { data: reportingInfraData } = useQuery(
    getReportingInfraProvisionedQuery,
    {
      variables: { workspaceId: selectedWorkspaceId },
      skip: isNil(selectedWorkspaceId) || !shouldGetWorkspaceReporting,
    }
  );

  const isWorkspaceDataLoading =
    loadingWorkspaces || loadingActiveWorkspace || loadingSelectedWorkspace;

  const decision = useDecidingWorkspaceAndChallenge(
    workspacesData,
    activeWorkspaceData,
    selectedWorkspaceData,
    selectedWorkspaceId,
    isAdmin
  );

  const reloadCurrentWorkspace = useCallback(() => {
    return new Promise((resolve, reject) => {
      setTimeout(async () => {
        try {
          if (selectedWorkspaceId) {
            await refetchSelectedWorkspace();
          } else {
            await refetchActiveWorkspace();
          }
          resolve();
        } catch (err) {
          // Always settle: a rejected refetch used to leave this promise
          // pending forever, hanging any `await reloadCurrentWorkspace()`.
          reject(err);
        }
      }, 1000);
    });
  }, [refetchActiveWorkspace, refetchSelectedWorkspace, selectedWorkspaceId]);

  const tagsVisibleIn = useTagsVisibleInFilter();
  const workspaceWithUpdatedTagTypesConfig = useMemo(() => {
    const workspace = decision?.workspace;
    if (!workspace) {
      return { config: { tagConfig: {} } };
    }

    const allTagTypesConfig = get(workspace, ["config", "tagConfig"], {});
    const tagTypesConfig = pickBy(
      allTagTypesConfig,
      (tagConfig) =>
        tagConfig?.isActive !== false &&
        includes(tagConfig?.visibleIn, tagsVisibleIn)
    );

    const workspaceConfig = cloneWith(workspace?.config, (config) => ({
      ...config,
      tagConfig: tagTypesConfig,
      unfilteredTagConfig: allTagTypesConfig,
    }));

    return cloneWith(workspace, (clonedWorkspace) => ({
      ...clonedWorkspace,
      config: workspaceConfig,
    }));
  }, [decision?.workspace, tagsVisibleIn]);

  const { config: tenantOrgConfig } = useOrgConfigForTenant();

  // Set default decimal places for the active workspace
  setDefaultDecimalPlaces(decision?.workspace?.config?.fteConfig?.decimal);

  const error =
    activeWorkspaceDataError ||
    selectedWorkspaceDataError ||
    workspacesQueryError;

  const workspaces = get(workspacesData, "workspaces", []);

  const values = useMemo(
    () => ({
      challenge: decision?.challenge || CHALLENGE_NONE,
      workspace: workspaceWithUpdatedTagTypesConfig || null,
      tenantOrgConfig: tenantOrgConfig || null,
      error,
      workspaces,
      isWorkspaceDataLoading,
      isReportingResourceProvisioned:
        reportingInfraData?.isReportingInfraProvisioned,
      reloadCurrentWorkspace,
    }),
    [
      decision,
      workspaceWithUpdatedTagTypesConfig,
      tenantOrgConfig,
      error,
      workspaces,
      isWorkspaceDataLoading,
      reportingInfraData,
      reloadCurrentWorkspace,
    ]
  );

  return <Provider {...values}>{children}</Provider>;
};

WorkspaceContextProvider.propTypes = {
  user: Auth0UserProps,
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]),
};

export default WorkspaceContextProvider;
